import { BaseSyntheticEvent, useEffect, useMemo } from 'react';
import {
  Backdrop,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { MailStub } from 'api/api.types';
import { MailReqBody } from 'api/invoices/invoices.types';
import { StyledButton } from 'components/Buttons';
import { StyledTextField } from 'components/Inputs';
import { TextEditor } from 'components/TextEditor';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { validateEmailArray } from 'utils/form.utils';
import { EditorWrap, InputWrap } from './elements/inputWrappers';
import { MailSelect } from './elements/MailSelect';

interface Props {
  header?: string;
  isOpen: boolean;
  onOpen: () => void;
  isLoading: boolean;
  mailStub?: MailStub;
  onSend: (data: MailReqBody) => void;
  isSendLoading?: boolean;
  onClose: () => void;
  counterpartyIds: number;
}

export const SendMailDialog = ({
  header,
  isOpen,
  onOpen,
  isLoading,
  mailStub,
  onSend,
  isSendLoading,
  onClose,
  counterpartyIds,
}: Props) => {
  const { t } = useTranslation(['mail', 'misc', 'validation']);

  useEffect(() => {
    isOpen && onOpen();
  }, [isOpen]); // adding onOpen causes inifnite rerenders

  const defaultValues = useMemo(
    () => ({
      from: mailStub?.from || '',
      replyTo: mailStub?.replyTo || '',
      to: mailStub?.to || [],
      cc: mailStub?.cc || [],
      bcc: mailStub?.bcc || [],
      subject: mailStub?.subject || '',
      text: mailStub?.text || '',
    }),
    [mailStub],
  );

  const {
    reset,
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<MailReqBody>({
    defaultValues,
    mode: 'onBlur',
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = (data: MailReqBody, _e?: BaseSyntheticEvent) => {
    onSend(data);
  };

  return (
    <Backdrop sx={{ zIndex: ({ zIndex }) => zIndex.drawer + 1 }} open={isOpen}>
      {isLoading && <CircularProgress />}

      <DialogStyled
        open={isOpen}
        onClose={onClose}
        maxWidth={false}
        $isHidden={isLoading}
        hideBackdrop
      >
        <Backdrop
          sx={{ zIndex: ({ zIndex }) => zIndex.drawer + 1 }}
          open={isSendLoading || isSubmitting}
        >
          <CircularProgress />
        </Backdrop>
        <form onSubmit={handleSubmit(onSubmit)}>
          {header && (
            <DialogTitle>{header || t('header', { ns: 'mail' })}</DialogTitle>
          )}
          <DialogContent>
            <Controller
              name='to'
              control={control}
              rules={{
                required: t('required', { ns: 'validation' }),
                validate: (value) =>
                  validateEmailArray(value) || t('email', { ns: 'validation' }),
              }}
              render={({
                field: { value, onChange, onBlur, name },
                fieldState: { error },
              }) => (
                <InputWrap label={t('to', { ns: 'mail' })} error={error}>
                  <MailSelect
                    name={name}
                    hiddenLabel
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={error}
                    counterpartyIds={counterpartyIds}
                  />
                </InputWrap>
              )}
            />
            <Controller
              name='cc'
              control={control}
              rules={{
                validate: (value) =>
                  validateEmailArray(value) || t('email', { ns: 'validation' }),
              }}
              render={({
                field: { value, onChange, onBlur, name },
                fieldState: { error },
              }) => (
                <InputWrap label={t('cc', { ns: 'mail' })} error={error}>
                  <MailSelect
                    name={name}
                    hiddenLabel
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={error}
                    counterpartyIds={counterpartyIds}
                    withSystemUsers
                  />
                </InputWrap>
              )}
            />
            <Controller
              name='bcc'
              control={control}
              rules={{
                validate: (value) =>
                  validateEmailArray(value) || t('email', { ns: 'validation' }),
              }}
              render={({
                field: { value, onChange, onBlur, name },
                fieldState: { error },
              }) => (
                <InputWrap label={t('bcc', { ns: 'mail' })} error={error}>
                  <MailSelect
                    name={name}
                    hiddenLabel
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={error}
                    counterpartyIds={counterpartyIds}
                    withSystemUsers
                  />
                </InputWrap>
              )}
            />
            <Controller
              name='subject'
              control={control}
              rules={{ required: t('required', { ns: 'validation' }) }}
              render={({ field, fieldState: { error } }) => (
                <InputWrap label={t('subject', { ns: 'mail' })} error={error}>
                  <StyledTextField
                    {...field}
                    hiddenLabel
                    size='small'
                    padding='dense'
                    margin='dense'
                  />
                </InputWrap>
              )}
            />
            <Controller
              name='text'
              control={control}
              rules={{ required: t('required', { ns: 'validation' }) }}
              render={({
                field: { value, onChange, onBlur },
                fieldState: { error },
              }) => (
                <EditorWrap error={error}>
                  <TextEditor
                    value={value}
                    onEditorChange={onChange}
                    onBlur={onBlur}
                  />
                </EditorWrap>
              )}
            />
          </DialogContent>

          <DialogActions>
            <StyledButton $primary type='submit'>
              {t('send', { ns: 'mail' })}
            </StyledButton>
            <StyledButton onClick={onClose}>
              {t('button.cancel', { ns: 'misc' })}
            </StyledButton>
          </DialogActions>
        </form>
      </DialogStyled>
    </Backdrop>
  );
};

const DialogStyled = styled(Dialog)<{ $isHidden?: boolean }>`
  ${({ $isHidden }) => $isHidden && ' visibility: hidden;'}
`;
